import * as microsoftTeams from "@microsoft/teams-js";
import { useEffect, useState } from "react";
import DOMPurify from "dompurify";

import Teams from "./TeamsTab";
import Web from "./WebTab";
import { Toaster } from 'react-hot-toast';
import Responsive from "../modules/Responsive/Responsive";
import { initFormSuccessHandler } from "../assets/demo/js/custom-script";
import { Login } from "../modules/apiUtils";
import { setTeamsContext } from "../config/AppConstants";
import {
  CALENDAR_ENTRY_SOURCE,
  setCalendarEntrySource,
} from "../modules/Calendar/Calendar/calendarEntrySource";
import {
  DELETE_ACCOUNT_RESPONSIVE_BYPASS_KEY,
  DELETE_ACCOUNT_STORAGE_KEY,
} from "../modules/DeleteAccount/DeleteAccount.jsx";
import Logo from "../assets/images/logo-big.svg";
import { useTranslation } from "react-i18next";

const TEAMS_LOCALE_TO_APP_LANGUAGE = {
  "en-us": "en",
  "en-gb": "en",
  "cs-cz": "cs",
  "nl-nl": "nl",
  "fr-fr": "fr",
  "de-de": "de",
  "it-it": "it",
  "pl-pl": "pl",
  "pt-pt": "pt",
  "es-es": "es",
};
const RONSPOT_FLEXWORK_URL = "https://ronspotflexwork.com/";
const RONSPOT_FLEXWORK_LABEL = "ronspotflexwork.com";

const resolveTeamsLanguage = (locale) => {
  const normalizedLocale = String(locale || "").trim().toLowerCase();

  if (!normalizedLocale) {
    return "en";
  }

  return (
    TEAMS_LOCALE_TO_APP_LANGUAGE[normalizedLocale] ||
    normalizedLocale.split("-")[0] ||
    "en"
  );
};

const formatReactUserMessage = (message) => {
  if (!message) {
    return "";
  }

  return String(message)
    .replace(/\*%s\*/g, RONSPOT_FLEXWORK_URL)
    .replace(/%s/g, RONSPOT_FLEXWORK_LABEL);
};

export default function CalendarEntryPoint(props) {
  const [msHomeAccountId, setMsHomeAccountId] = useState(null);
  const [isTeams, setIsTeams] = useState(false);
  const [isTeamsLoginReady, setIsTeamsLoginReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const shouldBypassResponsiveBlocker =
    window.location.pathname === "/accountremove" ||
    localStorage.getItem(DELETE_ACCOUNT_RESPONSIVE_BYPASS_KEY) === "1";
  const { t, i18n } = useTranslation("common");
  const reactUserMessageHtml = DOMPurify.sanitize(
    formatReactUserMessage(t("react_teams_no_ronspot_account_message"))
  ).replace(/<a /g, '<a target="_blank" rel="noreferrer" ');

  useEffect(() => {
    const cleanup = initFormSuccessHandler();

    return () => cleanup && cleanup();
  }, []);

  const getCalendarEntryErrorMessage = () => {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#f7f8fa",
        }}
      >
        <div
          style={{
            maxWidth: "520px",
            width: "50%",
            textAlign: "center",
            background: "#ffffff",
            borderRadius: "20px",
            padding: "40px 32px",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
          }}
        >
          <img
            src={Logo}
            alt="Upgrade plan"
            style={{ width: "200px", height: "70px" }}
          />
          <p
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: 600,
              color: "#1f2937",
            }}
            dangerouslySetInnerHTML={{ __html: reactUserMessageHtml }}
          />
          {/* <a
            href="https://ronspotflexwork.com/"
            target="_blank"
            rel="noreferrer"
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: 600,
              color: "#178377",
            }}
          >
            ronspotflexwork.com
          </a> */}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const init = async () => {
      let notifiedHost = false;
      try {
        const runningInIframe = window.self !== window.top;

        if (!runningInIframe) {
          setCalendarEntrySource(CALENDAR_ENTRY_SOURCE.WEB);
          setIsTeams(false);
          setIsLoading(false);
          return;
        }

        setCalendarEntrySource(CALENDAR_ENTRY_SOURCE.TEAMS);
        setIsTeams(true);
        await microsoftTeams.app.initialize();
        // Tell the Teams host the tab shell has loaded before running the slower
        // login/data calls. Without this signal the host keeps waiting and, if
        // login is slow (as on production), tears the tab down after ~50s with
        // "There was a problem reaching this app". Our own loading/error UI now
        // owns the login result instead of the host's load timeout.
        microsoftTeams.app.notifySuccess();
        notifiedHost = true;
        const context = await microsoftTeams.app.getContext();
        // Lets AppConstant.ManufactureModel include the Teams client type
        // (desktop / web / android / ios) in login payloads.
        setTeamsContext(context);
        const locale = context?.app?.locale || "en-US";
        const teamsLanguage = resolveTeamsLanguage(locale);

        await i18n.changeLanguage(teamsLanguage);
        console.log(locale, "LOCALE LAN");

        const accountId =
          context?.user?.id || context?.userObjectId;

        if (!accountId) {
          throw new Error("Microsoft Teams account ID not found");
        }

        sessionStorage.setItem("msHomeAccountId", accountId);

        console.log("Account ID in CalendarEntryPoint:", accountId);
        const loginResponse = await Login();

        if (!loginResponse || loginResponse.Data?.ResponseCode !== 200) {
          throw new Error("Unable to complete Teams login");
        }

        setMsHomeAccountId(accountId);
        setIsTeamsLoginReady(true);
      } catch (err) {
        console.error("Teams init failed:", err);
        // Only report failure to the host if we never signalled success (i.e. the
        // Teams SDK itself failed to initialize). Post-init failures (login/data)
        // are surfaced via the in-app error UI so the host keeps the tab mounted.
        if (!notifiedHost) {
          try {
            microsoftTeams.app.notifyFailure({
              reason: microsoftTeams.app.FailedReason.Other,
              message: err?.message,
            });
          } catch (notifyErr) {
            console.error("notifyFailure failed:", notifyErr);
          }
        }
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [i18n]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (hasError) {
    return getCalendarEntryErrorMessage();
  }

  if (!isTeams) {
    return <div>
      <Toaster position="top-right" reverseOrder={false} />
      {/* <MyMapComponent /> */}
      <Web />
      {!shouldBypassResponsiveBlocker && <Responsive />}
    </div>
    // <Router>
    //  <ProtectedRoute>
    //  <Web />;
    // </ProtectedRoute>
    // </Router>
  }

  if (msHomeAccountId && isTeamsLoginReady) {
    return <Teams {...props} />;
  }
}
